/**
 * RateLimitError — Rate Limit 도달 시 InputBar 교체 UI
 *
 * - 사용량 표시 + 리셋 타이머
 * - 업그레이드 CTA (P0)
 */

import { Zap, Clock, ArrowUpCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/shared/lib/utils";

export interface RateLimitErrorProps {
  message?: string;
  resetAt?: number; // epoch ms
  onUpgrade?: () => void;
  onDismiss?: () => void;
  className?: string;
}

function useCountdown(resetAt?: number) {
  const [timeLeft, setTimeLeft] = useState<string>("");

  useEffect(() => {
    if (!resetAt) return;

    function update() {
      const diff = resetAt! - Date.now();
      if (diff <= 0) {
        setTimeLeft("지금 바로 사용 가능");
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeLeft(`${hours}시간 ${mins}분 후 초기화`);
      } else if (mins > 0) {
        setTimeLeft(`${mins}분 ${secs}초 후 초기화`);
      } else {
        setTimeLeft(`${secs}초 후 초기화`);
      }
    }

    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [resetAt]);

  return timeLeft;
}

export function RateLimitError({
  message = "이번 달 AI 사용 한도에 도달했습니다.",
  resetAt,
  onUpgrade,
  onDismiss,
  className,
}: RateLimitErrorProps) {
  const countdown = useCountdown(resetAt);

  return (
    <div
      className={cn(
        "mx-4 mt-3 rounded-xl border border-amber-200/60 bg-amber-50/80 px-4 py-3.5",
        "dark:border-amber-800/40 dark:bg-amber-950/20",
        className,
      )}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <Zap className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex-1 space-y-1.5">
          <p className="text-[13px] font-medium text-amber-800 dark:text-amber-200">
            {message}
          </p>

          {countdown && (
            <div className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
              <Clock className="size-3" />
              <span>{countdown}</span>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            {/* P0 업그레이드 CTA */}
            <button
              type="button"
              onClick={onUpgrade}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-[13px] font-semibold text-white shadow-sm hover:bg-emerald-700 transition-colors"
            >
              <ArrowUpCircle className="size-4" />
              <span>플랜 업그레이드</span>
            </button>

            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-[12px] font-medium text-amber-700 hover:bg-amber-50 transition-colors dark:bg-transparent dark:text-amber-400"
              >
                닫기
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
